import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useRef, useCallback } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Sun, Moon, CheckCircle2, Circle, AlertTriangle, Lightbulb, Send,
  Clock, ArrowRight, Sparkles, ListChecks, MessageSquare, Mail,
  MailOpen, Loader2, RefreshCw, Brain,
} from "lucide-react";
import ReactMarkdown from "react-markdown";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

interface BriefingMessage {
  role: "user" | "assistant";
  content: string;
}

interface UnansweredEmail {
  id: string;
  subject: string;
  sender_name: string;
  sender_email: string;
  received_at: string;
  preview: string;
}

interface BriefingData {
  date: string;
  unanswered_emails: UnansweredEmail[];
  total_received_today: number;
  total_sent_today: number;
  ai_summary: string;
}

const defaultPriorities = [
  { id: 1, label: "Chargement des priorités...", priority: "moyenne", done: false, link: "" },
];

export default function BriefingDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => {
    const hour = new Date().getHours();
    return hour >= 17 ? "soir" : "matin";
  });
  const [priorities, setPriorities] = useState(defaultPriorities);
  const [morningStats, setMorningStats] = useState<any>(null);
  const [chatMessages, setChatMessages] = useState<BriefingMessage[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Evening briefing state
  const [briefing, setBriefing] = useState<BriefingData | null>(null);
  const [briefingLoading, setBriefingLoading] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
  }, [user, loading, navigate]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // Load dynamic morning priorities
  const loadMorningPriorities = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/briefing/morning`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setPriorities((data.priorities || []).map((p: any) => ({ ...p, done: false })));
        setMorningStats(data.stats || null);
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (user && activeTab === "matin") {
      loadMorningPriorities();
    }
  }, [user, activeTab, loadMorningPriorities]);

  const loadBriefing = useCallback(async () => {
    setBriefingLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/briefing/evening`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setBriefing(data);
      }
    } catch (err) {
      console.error("Briefing error:", err);
    }
    setBriefingLoading(false);
  }, []);

  useEffect(() => {
    if (user && activeTab === "soir") {
      loadBriefing();
    }
  }, [user, activeTab, loadBriefing]);

  const toggleDone = (id: number) => {
    setPriorities((prev) =>
      prev.map((p) => (p.id === id ? { ...p, done: !p.done } : p))
    );
  };

  const priorityColor = (p: string) => {
    if (p === "haute") return "text-red-500 bg-red-500/10 border-red-500/20";
    if (p === "moyenne") return "text-yellow-500 bg-yellow-500/10 border-yellow-500/20";
    return "text-green-500 bg-green-500/10 border-green-500/20";
  };

  const sendChat = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const userMsg: BriefingMessage = { role: "user", content: chatInput };
    setChatMessages((prev) => [...prev, userMsg]);
    setChatInput("");
    setChatLoading(true);

    try {
      const context = activeTab === "soir" && briefing
        ? `Briefing du soir. Emails sans réponse: ${briefing.unanswered_emails.length}. Reçus: ${briefing.total_received_today}. Envoyés: ${briefing.total_sent_today}. Emails non répondus: ${briefing.unanswered_emails.map(e => `${e.sender_name}: ${e.subject}`).join(", ")}`
        : `Briefing du matin. Actions: ${priorities.map(p => `${p.label} (${p.done ? "fait" : "à faire"})`).join(", ")}`;

      const res = await fetch(`${API_URL}/api/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: chatInput,
          context: context,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setChatMessages((prev) => [...prev, { role: "assistant", content: data.response || data.message || "Je suis là pour vous aider." }]);
      } else {
        throw new Error("Chat error");
      }
    } catch {
      setChatMessages((prev) => [...prev, { role: "assistant", content: "Erreur de connexion. Réessayez." }]);
    } finally {
      setChatLoading(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background text-foreground"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (!user) return null;

  const doneCount = priorities.filter((p) => p.done).length;
  const progress = Math.round((doneCount / priorities.length) * 100);

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <DashboardSidebar />
        <div className="flex-1 flex flex-col">
          <header className="h-14 border-b border-border flex items-center justify-between px-4">
            <div className="flex items-center gap-3">
              <SidebarTrigger />
              <Sparkles className="h-5 w-5 text-primary" />
              <h1 className="text-lg font-bold text-foreground">Briefing Grafton</h1>
              <Badge variant="outline" className="text-xs" data-testid="briefing-date">
                {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
              </Badge>
            </div>
          </header>

          <main className="flex-1 p-6 overflow-auto">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
              <TabsList className="grid w-full max-w-md grid-cols-2">
                <TabsTrigger value="matin" className="gap-2" data-testid="tab-matin">
                  <Sun className="h-4 w-4" /> Briefing du Matin
                </TabsTrigger>
                <TabsTrigger value="soir" className="gap-2" data-testid="tab-soir">
                  <Moon className="h-4 w-4" /> Briefing du Soir
                </TabsTrigger>
              </TabsList>

              {/* ===== MATIN ===== */}
              <TabsContent value="matin" className="space-y-6">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-foreground">Progression du jour</span>
                      <span className="text-sm text-muted-foreground">{doneCount}/{priorities.length} actions</span>
                    </div>
                    <div className="h-3 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <ListChecks className="h-5 w-5 text-primary" />
                      Actions prioritaires du jour
                    </CardTitle>
                    <CardDescription>Analysées et classées par Grafton IA</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {priorities.map((item: any) => (
                      <div
                        key={item.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-all cursor-pointer ${
                          item.done ? "bg-muted/50 opacity-60" : "bg-card hover:bg-accent/30"
                        }`}
                        onClick={() => toggleDone(item.id)}
                        data-testid={`priority-${item.id}`}
                      >
                        {item.done ? (
                          <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                        ) : (
                          <Circle className="h-5 w-5 text-muted-foreground shrink-0" />
                        )}
                        <span className={`flex-1 text-sm ${item.done ? "line-through text-muted-foreground" : "text-foreground"}`}>
                          {item.label}
                        </span>
                        <Badge variant="outline" className={`text-xs ${priorityColor(item.priority)}`}>
                          {item.priority}
                        </Badge>
                        {item.link && !item.done && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[10px] h-6 px-2 shrink-0"
                            onClick={(e) => { e.stopPropagation(); navigate(item.link); }}
                          >
                            <ArrowRight className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Chat Matin */}
                <ChatCard
                  messages={chatMessages}
                  input={chatInput}
                  loading={chatLoading}
                  onInputChange={setChatInput}
                  onSend={sendChat}
                  chatEndRef={chatEndRef}
                  placeholder="Ex: Quelle est ma priorité n°1 aujourd'hui ?"
                  emptyIcon={<Sparkles className="h-4 w-4 mr-2" />}
                  emptyText="Bonjour ! Prêt à organiser votre journée ?"
                />
              </TabsContent>

              {/* ===== SOIR ===== */}
              <TabsContent value="soir" className="space-y-6" data-testid="briefing-soir-content">
                {briefingLoading ? (
                  <div className="flex flex-col items-center justify-center py-16">
                    <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                    <p className="text-sm text-muted-foreground">Analyse de votre journée en cours...</p>
                  </div>
                ) : briefing ? (
                  <>
                    {/* KPIs */}
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                      <Card className="border-sky-500/20" data-testid="briefing-received">
                        <CardContent className="pt-6 text-center">
                          <Mail className="h-8 w-8 text-sky-500 mx-auto mb-2" />
                          <div className="text-2xl font-bold text-foreground">{briefing.total_received_today}</div>
                          <p className="text-sm text-muted-foreground">Reçus aujourd'hui</p>
                        </CardContent>
                      </Card>
                      <Card className="border-green-500/20" data-testid="briefing-sent">
                        <CardContent className="pt-6 text-center">
                          <Send className="h-8 w-8 text-green-500 mx-auto mb-2" />
                          <div className="text-2xl font-bold text-foreground">{briefing.total_sent_today}</div>
                          <p className="text-sm text-muted-foreground">Envoyés aujourd'hui</p>
                        </CardContent>
                      </Card>
                      <Card className="border-amber-500/20" data-testid="briefing-unanswered">
                        <CardContent className="pt-6 text-center">
                          <MailOpen className="h-8 w-8 text-amber-500 mx-auto mb-2" />
                          <div className="text-2xl font-bold text-foreground">{briefing.unanswered_emails.length}</div>
                          <p className="text-sm text-muted-foreground">Sans réponse</p>
                        </CardContent>
                      </Card>
                      <Card className={briefing.unanswered_emails.length === 0 ? "border-green-500/20" : "border-red-500/20"}>
                        <CardContent className="pt-6 text-center">
                          {briefing.unanswered_emails.length === 0 ? (
                            <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
                          ) : (
                            <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
                          )}
                          <div className="text-2xl font-bold text-foreground">
                            {briefing.unanswered_emails.length === 0 ? "OK" : briefing.unanswered_emails.length}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {briefing.unanswered_emails.length === 0 ? "Tout traité" : "Actions requises"}
                          </p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* AI Summary */}
                    <Card className="border-primary/20 bg-primary/[0.02]" data-testid="briefing-ai-summary">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-base">
                          <Brain className="h-5 w-5 text-primary" />
                          Synthèse IA de votre journée
                        </CardTitle>
                        <CardDescription>Analyse automatique par Grafton IA</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="prose prose-sm prose-invert prose-p:text-white prose-li:text-white prose-headings:text-white prose-strong:text-primary max-w-none">
                          <ReactMarkdown>{briefing.ai_summary}</ReactMarkdown>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Unanswered emails */}
                    {briefing.unanswered_emails.length > 0 && (
                      <Card data-testid="briefing-unanswered-list">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2 text-base">
                            <MailOpen className="h-5 w-5 text-amber-500" />
                            Emails sans réponse ({briefing.unanswered_emails.length})
                          </CardTitle>
                          <CardDescription>Ces emails attendent votre réponse depuis aujourd'hui</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          {briefing.unanswered_emails.map((email) => (
                            <div
                              key={email.id}
                              className="flex items-start gap-3 p-3 rounded-lg border border-amber-500/20 bg-amber-500/5 hover:bg-amber-500/10 transition-colors cursor-pointer"
                              onClick={() => navigate("/emails")}
                              data-testid={`unanswered-email-${email.id}`}
                            >
                              <MailOpen className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-sm font-semibold truncate">{email.sender_name}</span>
                                  <span className="text-xs text-muted-foreground shrink-0">
                                    {email.received_at ? new Date(email.received_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : ""}
                                  </span>
                                </div>
                                <p className="text-sm text-foreground truncate">{email.subject}</p>
                                <p className="text-xs text-muted-foreground truncate">{email.preview}</p>
                              </div>
                              <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    )}

                    {/* Refresh button */}
                    <div className="flex justify-center">
                      <Button variant="outline" className="gap-2" onClick={loadBriefing} data-testid="briefing-refresh">
                        <RefreshCw className="h-4 w-4" /> Actualiser le briefing
                      </Button>
                    </div>

                    {/* Chat Soir */}
                    <ChatCard
                      messages={chatMessages}
                      input={chatInput}
                      loading={chatLoading}
                      onInputChange={setChatInput}
                      onSend={sendChat}
                      chatEndRef={chatEndRef}
                      placeholder="Ex: Quels emails dois-je traiter en priorité demain matin ?"
                      emptyIcon={<Moon className="h-4 w-4 mr-2" />}
                      emptyText="Bonsoir ! Faisons le point sur votre journée."
                    />
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <Moon className="h-12 w-12 text-muted-foreground/20 mb-4" />
                    <p className="text-muted-foreground">Impossible de charger le briefing.</p>
                    <Button variant="outline" className="mt-4 gap-2" onClick={loadBriefing}>
                      <RefreshCw className="h-4 w-4" /> Réessayer
                    </Button>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}

// ── Reusable Chat Card ──
function ChatCard({
  messages, input, loading, onInputChange, onSend, chatEndRef, placeholder, emptyIcon, emptyText,
}: {
  messages: BriefingMessage[];
  input: string;
  loading: boolean;
  onInputChange: (v: string) => void;
  onSend: () => void;
  chatEndRef: React.RefObject<HTMLDivElement>;
  placeholder: string;
  emptyIcon: React.ReactNode;
  emptyText: string;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageSquare className="h-5 w-5 text-primary" />
          Discuter avec Grafton
        </CardTitle>
        <CardDescription>Posez vos questions, ajustez vos priorités</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-64 overflow-y-auto space-y-3 mb-3 p-3 bg-muted/30 rounded-lg" data-testid="briefing-chat">
          {messages.length === 0 && (
            <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
              {emptyIcon}
              {emptyText}
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                msg.role === "user" ? "bg-primary text-primary-foreground" : "bg-card border text-white"
              }`}>
                {msg.role === "assistant" ? (
                  <div className="prose prose-sm prose-invert prose-p:text-white prose-li:text-white prose-headings:text-white prose-strong:text-primary max-w-none"><ReactMarkdown>{msg.content}</ReactMarkdown></div>
                ) : msg.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-card border rounded-lg px-3 py-2 text-sm text-muted-foreground animate-pulse">
                Grafton réfléchit...
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
        <div className="flex gap-2">
          <input
            className="flex-1 rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder={placeholder}
            value={input}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onSend()}
            data-testid="briefing-chat-input"
          />
          <Button size="sm" onClick={onSend} disabled={loading} data-testid="briefing-chat-send">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
